import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useFamily } from '../contexts/FamilyContext';

const FamilyDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentFamily, refreshFamilies } = useFamily();
  const [family, setFamily] = useState(null);
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [wishlists, setWishlists] = useState([]);
  const [namePickerGroups, setNamePickerGroups] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [savingColors, setSavingColors] = useState(false);
  const [colorChanges, setColorChanges] = useState({});
  const [backgroundImageFile, setBackgroundImageFile] = useState(null);
  const [backgroundImagePreview, setBackgroundImagePreview] = useState(null);
  const [googleCalendarEmbedUrl, setGoogleCalendarEmbedUrl] = useState('');

  const fetchFamilyData = useCallback(async () => {
    if (!currentFamily) return;
    try {
      const response = await api.get(`/families/${currentFamily.id}/`);
      setFamily(response.data);
      setGoogleCalendarEmbedUrl(response.data.google_calendar_embed_url || '');
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, [currentFamily]);

  const fetchStats = useCallback(async () => {
    if (!currentFamily) return;
    try {
      const response = await api.get(`/families/${currentFamily.id}/stats/`);
      setStats(response.data);
    } catch (error) {
    }
  }, [currentFamily]);

  const fetchEvents = useCallback(async () => {
    if (!currentFamily) return;
    try {
      const response = await api.get(`/events/?family_id=${currentFamily.id}`);
      setEvents(response.data.results || response.data);
    } catch (error) {
    }
  }, [currentFamily]);

  const fetchWishlists = useCallback(async () => {
    if (!currentFamily) return;
    try {
      const response = await api.get(`/wishlists/?family_id=${currentFamily.id}`);
      setWishlists(response.data.results || response.data);
    } catch (error) {
    }
  }, [currentFamily]);

  const fetchNamePickerGroups = useCallback(async () => {
    if (!currentFamily) return;
    try {
      const response = await api.get(`/name-picker/?family_id=${currentFamily.id}`);
      setNamePickerGroups(response.data.results || response.data);
    } catch (error) {
    }
  }, [currentFamily]);

  const fetchRecipes = useCallback(async () => {
    if (!currentFamily) return;
    try {
      const response = await api.get(`/recipes/?family_id=${currentFamily.id}`);
      setRecipes(response.data.results || response.data);
    } catch (error) {
    }
  }, [currentFamily]);

  useEffect(() => {
    if (currentFamily) {
      fetchFamilyData();
      fetchStats();
      fetchEvents();
      fetchWishlists();
      fetchNamePickerGroups();
      fetchRecipes();
    }
  }, [currentFamily, fetchFamilyData, fetchStats, fetchEvents, fetchWishlists, fetchNamePickerGroups, fetchRecipes]);

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await api.delete(`/events/${eventId}/delete_event/`);
      fetchEvents();
      fetchStats();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete event');
    }
  };

  const handleDeleteWishlist = async (wishlistId) => {
    if (!window.confirm('Are you sure you want to delete this wishlist?')) return;
    
    try {
      await api.delete(`/wishlists/${wishlistId}/delete_wishlist/`);
      fetchWishlists();
      fetchStats();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete wishlist');
    }
  };

  const handleDeleteNamePickerGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this name picker group?')) return;
    
    try {
      await api.delete(`/name-picker/${groupId}/delete_group/`);
      fetchNamePickerGroups();
      fetchStats();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete name picker group');
    }
  };

  const handleDeleteRecipe = async (recipeId) => {
    if (!window.confirm('Are you sure you want to delete this recipe?')) return;
    
    try {
      await api.delete(`/recipes/${recipeId}/delete_recipe/`);
      fetchRecipes();
      fetchStats();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete recipe');
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;
    
    try {
      await api.delete(`/families/${currentFamily.id}/members/${memberId}/`);
      await refreshFamilies();
      fetchFamilyData();
      fetchStats();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to remove member');
    }
  };

  const handleUpdateRole = async () => {
    try {
      await api.post(`/families/${currentFamily.id}/members/${selectedMember.id}/role/`, {
        role: newRole,
      });
      await refreshFamilies();
      fetchFamilyData();
      setRoleDialogOpen(false);
      setSelectedMember(null);
      setNewRole('');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update role');
    }
  };

  const handleColorChange = (colorType, value) => {
    setColorChanges({
      ...colorChanges,
      [colorType]: value,
    });
    // Update local state for immediate preview
    setFamily({
      ...family,
      [colorType]: value,
    });
  };

  const handleSaveColors = async () => {
    if (!currentFamily) return;
    
    setSavingColors(true);
    try {
      // Use FormData if there's an image file, otherwise use JSON
      if (backgroundImageFile) {
        const formData = new FormData();
        // Add color changes
        Object.keys(colorChanges).forEach(key => {
          formData.append(key, colorChanges[key]);
        });
        // Add background image
        formData.append('background_image', backgroundImageFile);
        formData.append('google_calendar_embed_url', googleCalendarEmbedUrl);

        await api.post(`/families/${currentFamily.id}/update-colors/`, formData);
      } else {
        // If no image file but we want to clear the image
        const dataToSend = { ...colorChanges, google_calendar_embed_url: googleCalendarEmbedUrl };
        if (backgroundImagePreview === '' && family?.background_image_url) {
          // User wants to clear the image (set to empty string)
          dataToSend.background_image = '';
        }
        await api.post(`/families/${currentFamily.id}/update-colors/`, dataToSend);
      }
      
      await refreshFamilies();
      fetchFamilyData();
      setColorChanges({});
      setBackgroundImageFile(null);
      setBackgroundImagePreview(null);
      alert('Theme updated successfully!');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update theme');
      // Revert changes on error
      fetchFamilyData();
    } finally {
      setSavingColors(false);
    }
  };

  const handleResetColors = async () => {
    if (!window.confirm('Reset all colors to default boho theme?')) return;
    if (!currentFamily) return;
    
    setSavingColors(true);
    try {
      await api.post(`/families/${currentFamily.id}/reset-colors/`);
      await refreshFamilies();
      fetchFamilyData();
      setColorChanges({});
      alert('Colors reset to default!');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to reset colors');
    } finally {
      setSavingColors(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (!family) {
    return (
      <Container>
        <Typography>Family not found</Typography>
      </Container>
    );
  }

  const userMember = family.members?.find((m) => m.user_id === user?.uid);
  const isAdmin = userMember && ['owner', 'admin'].includes(userMember.role);

  if (!isAdmin) {
    return (
      <Container>
        <Alert severity="error">
          You must be a family admin or owner to access this dashboard.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, mb: 4, px: { xs: 1, sm: 2 } }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={2}
        sx={{
          mb: 3,
          bgcolor: 'background.paper',
          borderRadius: 3,
          p: { xs: 1.5, sm: 2 },
          boxShadow: '0 2px 12px rgba(255, 255, 255, 0.6)',
        }}
      >
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
          {family.name} Dashboard
        </Typography>
        <Button onClick={() => navigate('/families')} variant="outlined" fullWidth={{ xs: true, sm: false }}>
          Back to Families
        </Button>
      </Box>

      {userMember.role === 'owner' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You are the owner of this family. You have full administrative access.
        </Alert>
      )}

      {userMember.role === 'admin' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You are an admin of this family. You can manage events, wishlists, and members.
        </Alert>
      )}

      {/* Statistics */}
      {stats && (
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
          <Grid item xs={6} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
                  {stats.member_count}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Members
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4">{stats.event_count}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Events
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4">{stats.wishlist_count}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Wishlists
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4">{stats.name_picker_count}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Name Picker Groups
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4">{stats.recipe_count}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Recipes
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4">{stats.budget_count}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Budgets
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4">{stats.gift_record_count}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Gift Records
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {/* Members Management */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Family Members
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {family.members?.map((member) => (
                      <TableRow key={member.id}>
                        <TableCell>{member.user_name}</TableCell>
                        <TableCell>{member.user_email || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                            size="small"
                            sx={{
                              backgroundColor: member.role === 'owner' ? '#7A9A8C' : member.role === 'admin' ? '#D4B896' : '#E8DDC8',
                              color: member.role === 'owner' ? '#FFFFFF' : '#6B5A4A',
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {userMember.role === 'owner' && member.role !== 'owner' && (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedMember(member);
                                  setNewRole(member.role);
                                  setRoleDialogOpen(true);
                                }}
                                title="Edit Role"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveMember(member.id)}
                                color="error"
                                title="Remove Member"
                              >
                                <PersonRemoveIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                          {member.role === 'owner' && (
                            <Chip label="Owner" size="small" sx={{ backgroundColor: '#7A9A8C', color: '#FFFFFF' }} />
                          )}
                          {userMember.role === 'admin' && member.role === 'member' && (
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveMember(member.id)}
                              color="error"
                              title="Remove Member"
                            >
                              <PersonRemoveIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Events Management */}
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Events
              </Typography>
              {events.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No events yet
                </Typography>
              ) : (
                <List dense>
                  {events.slice(0, 5).map((event) => (
                    <ListItem
                      key={event.id}
                      secondaryAction={
                        <Box>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/events/${event.id}`)}
                            title="View Event"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteEvent(event.id)}
                            color="error"
                            title="Delete Event"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            {event.title}
                            {event.event_type && (
                              <Chip
                                label={event.event_type}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                        secondary={new Date(event.event_date).toLocaleDateString()}
                      />
                    </ListItem>
                  ))}
                  {events.length > 5 && (
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 2, pt: 1 }}>
                      +{events.length - 5} more events
                    </Typography>
                  )}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Wishlists Management */}
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Wishlists
              </Typography>
              {wishlists.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No wishlists yet
                </Typography>
              ) : (
                <List dense>
                  {wishlists.slice(0, 5).map((wishlist) => (
                    <ListItem
                      key={wishlist.id}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => handleDeleteWishlist(wishlist.id)}
                          color="error"
                          size="small"
                          title="Delete Wishlist"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={wishlist.title}
                        secondary={`${wishlist.items?.length || 0} items • ${wishlist.user_name}`}
                      />
                    </ListItem>
                  ))}
                  {wishlists.length > 5 && (
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 2, pt: 1 }}>
                      +{wishlists.length - 5} more wishlists
                    </Typography>
                  )}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Name Picker Groups */}
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Name Picker Groups
              </Typography>
              {namePickerGroups.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No groups yet
                </Typography>
              ) : (
                <List dense>
                  {namePickerGroups.slice(0, 5).map((group) => (
                    <ListItem
                      key={group.id}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => handleDeleteNamePickerGroup(group.id)}
                          color="error"
                          size="small"
                          title="Delete Group"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={group.name}
                        secondary={`${group.participant_count} participants${group.is_drawn ? ' • Names drawn' : ''}`}
                      />
                    </ListItem>
                  ))}
                  {namePickerGroups.length > 5 && (
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 2, pt: 1 }}>
                      +{namePickerGroups.length - 5} more groups
                    </Typography>
                  )}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recipes */}
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recipes
              </Typography>
              {recipes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No recipes yet
                </Typography>
              ) : (
                <List dense>
                  {recipes.slice(0, 5).map((recipe) => (
                    <ListItem
                      key={recipe.id}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => handleDeleteRecipe(recipe.id)}
                          color="error"
                          size="small"
                          title="Delete Recipe"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={recipe.title}
                        secondary={recipe.created_by_name}
                      />
                    </ListItem>
                  ))}
                  {recipes.length > 5 && (
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 2, pt: 1 }}>
                      +{recipes.length - 5} more recipes
                    </Typography>
                  )}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Family Settings */}
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Family Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Name: {family.name}
              </Typography>
              {family.description && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Description: {family.description}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Subscription: {family.subscription_tier}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Max Members: {family.max_members}
              </Typography>
              {family.subscription_tier === 'free' && (
                <Button
                  variant="contained"
                  size="small"
                  sx={{ mt: 2, mb: 1 }}
                  onClick={() => navigate('/subscription')}
                >
                  Upgrade Subscription
                </Button>
              )}
              {family.subscription_tier !== 'free' && (
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 2, mb: 1 }}
                  onClick={() => navigate('/subscription')}
                >
                  Manage Subscription
                </Button>
              )}
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate(`/families/${family.id}/feed`)}
                >
                  View Family Feed
                </Button>
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => {
                    if (!family.invite_token) {
                      alert('No invite token available. Please refresh the page.');
                      return;
                    }
                    const url = `${window.location.origin}/families/join/${family.invite_token}`;
                    navigator.clipboard.writeText(url);
                    alert('Invite link copied to clipboard! Share this link with family members to invite them.');
                  }}
                  sx={{ mt: 1 }}
                >
                  Copy Invite Link
                </Button>
                {family.invite_token ? (
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: 'rgba(0, 0, 0, 0.05)', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Invite Token:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace', 
                          wordBreak: 'break-all',
                          flex: 1,
                          fontWeight: 500,
                          color: 'text.primary'
                        }}
                      >
                        {family.invite_token}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          navigator.clipboard.writeText(family.invite_token);
                          alert('Token copied to clipboard!');
                        }}
                        title="Copy token"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Share this token or the full link above
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    No invite token available. Please refresh the page.
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Color Customization */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Customize Colors
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Customize the color scheme for your family. Changes will be visible to all members.
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Primary Color
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                      <input
                        type="color"
                        value={family.primary_color || '#7A9A8C'}
                        onChange={(e) => handleColorChange('primary_color', e.target.value)}
                        style={{
                          width: '60px',
                          height: '40px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      />
                      <TextField
                        size="small"
                        value={family.primary_color || '#7A9A8C'}
                        onChange={(e) => handleColorChange('primary_color', e.target.value)}
                        placeholder="#7A9A8C"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Secondary Color
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                      <input
                        type="color"
                        value={family.secondary_color || '#D4C5A9'}
                        onChange={(e) => handleColorChange('secondary_color', e.target.value)}
                        style={{
                          width: '60px',
                          height: '40px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      />
                      <TextField
                        size="small"
                        value={family.secondary_color || '#D4C5A9'}
                        onChange={(e) => handleColorChange('secondary_color', e.target.value)}
                        placeholder="#D4C5A9"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Background Color
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                      <input
                        type="color"
                        value={family.background_color || '#F5F1E8'}
                        onChange={(e) => handleColorChange('background_color', e.target.value)}
                        style={{
                          width: '60px',
                          height: '40px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      />
                      <TextField
                        size="small"
                        value={family.background_color || '#F5F1E8'}
                        onChange={(e) => handleColorChange('background_color', e.target.value)}
                        placeholder="#F5F1E8"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Primary Text Color
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                      <input
                        type="color"
                        value={family.text_color || '#5A5A5A'}
                        onChange={(e) => handleColorChange('text_color', e.target.value)}
                        style={{
                          width: '60px',
                          height: '40px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      />
                      <TextField
                        size="small"
                        value={family.text_color || '#5A5A5A'}
                        onChange={(e) => handleColorChange('text_color', e.target.value)}
                        placeholder="#5A5A5A"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Secondary Text Color
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                      <input
                        type="color"
                        value={family.text_secondary_color || '#8B7D6B'}
                        onChange={(e) => handleColorChange('text_secondary_color', e.target.value)}
                        style={{
                          width: '60px',
                          height: '40px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      />
                      <TextField
                        size="small"
                        value={family.text_secondary_color || '#8B7D6B'}
                        onChange={(e) => handleColorChange('text_secondary_color', e.target.value)}
                        placeholder="#8B7D6B"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Button Text Color
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                      <input
                        type="color"
                        value={family.button_text_color || '#FFFFFF'}
                        onChange={(e) => handleColorChange('button_text_color', e.target.value)}
                        style={{
                          width: '60px',
                          height: '40px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      />
                      <TextField
                        size="small"
                        value={family.button_text_color || '#FFFFFF'}
                        onChange={(e) => handleColorChange('button_text_color', e.target.value)}
                        placeholder="#FFFFFF"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Error Color
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                      <input
                        type="color"
                        value={family.error_color || '#C99A7A'}
                        onChange={(e) => handleColorChange('error_color', e.target.value)}
                        style={{
                          width: '60px',
                          height: '40px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      />
                      <TextField
                        size="small"
                        value={family.error_color || '#C99A7A'}
                        onChange={(e) => handleColorChange('error_color', e.target.value)}
                        placeholder="#C99A7A"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Success Color
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                      <input
                        type="color"
                        value={family.success_color || '#9DB4A8'}
                        onChange={(e) => handleColorChange('success_color', e.target.value)}
                        style={{
                          width: '60px',
                          height: '40px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      />
                      <TextField
                        size="small"
                        value={family.success_color || '#9DB4A8'}
                        onChange={(e) => handleColorChange('success_color', e.target.value)}
                        placeholder="#9DB4A8"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Warning Color
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                      <input
                        type="color"
                        value={family.warning_color || '#D4B896'}
                        onChange={(e) => handleColorChange('warning_color', e.target.value)}
                        style={{
                          width: '60px',
                          height: '40px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      />
                      <TextField
                        size="small"
                        value={family.warning_color || '#D4B896'}
                        onChange={(e) => handleColorChange('warning_color', e.target.value)}
                        placeholder="#D4B896"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Info Color
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                      <input
                        type="color"
                        value={family.info_color || '#7A9A8C'}
                        onChange={(e) => handleColorChange('info_color', e.target.value)}
                        style={{
                          width: '60px',
                          height: '40px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      />
                      <TextField
                        size="small"
                        value={family.info_color || '#7A9A8C'}
                        onChange={(e) => handleColorChange('info_color', e.target.value)}
                        placeholder="#7A9A8C"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      App Bar / Navbar Color
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                      <input
                        type="color"
                        value={family.appbar_color || '#7A9A8C'}
                        onChange={(e) => handleColorChange('appbar_color', e.target.value)}
                        style={{
                          width: '60px',
                          height: '40px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      />
                      <TextField
                        size="small"
                        value={family.appbar_color || '#7A9A8C'}
                        onChange={(e) => handleColorChange('appbar_color', e.target.value)}
                        placeholder="#7A9A8C"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Link Color
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                      <input
                        type="color"
                        value={family.link_color || '#7A9A8C'}
                        onChange={(e) => handleColorChange('link_color', e.target.value)}
                        style={{
                          width: '60px',
                          height: '40px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      />
                      <TextField
                        size="small"
                        value={family.link_color || '#7A9A8C'}
                        onChange={(e) => handleColorChange('link_color', e.target.value)}
                        placeholder="#7A9A8C"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Icon Color
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                      <input
                        type="color"
                        value={family.icon_color || '#B8956A'}
                        onChange={(e) => handleColorChange('icon_color', e.target.value)}
                        style={{
                          width: '60px',
                          height: '40px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      />
                      <TextField
                        size="small"
                        value={family.icon_color || '#B8956A'}
                        onChange={(e) => handleColorChange('icon_color', e.target.value)}
                        placeholder="#B8956A"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Used for feature icons on the home page
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Primary Button Color
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                      <input
                        type="color"
                        value={family.button_primary_color || '#7A9A8C'}
                        onChange={(e) => handleColorChange('button_primary_color', e.target.value)}
                        style={{
                          width: '60px',
                          height: '40px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      />
                      <TextField
                        size="small"
                        value={family.button_primary_color || '#7A9A8C'}
                        onChange={(e) => handleColorChange('button_primary_color', e.target.value)}
                        placeholder="#7A9A8C"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Color for main/contained buttons
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Secondary Button Color
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                      <input
                        type="color"
                        value={family.button_secondary_color || '#D4C5A9'}
                        onChange={(e) => handleColorChange('button_secondary_color', e.target.value)}
                        style={{
                          width: '60px',
                          height: '40px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      />
                      <TextField
                        size="small"
                        value={family.button_secondary_color || '#D4C5A9'}
                        onChange={(e) => handleColorChange('button_secondary_color', e.target.value)}
                        placeholder="#D4C5A9"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Color for outlined/secondary buttons
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Footer Color
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                      <input
                        type="color"
                        value={family.footer_color || '#9D8B70'}
                        onChange={(e) => handleColorChange('footer_color', e.target.value)}
                        style={{
                          width: '60px',
                          height: '40px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      />
                      <TextField
                        size="small"
                        value={family.footer_color || '#9D8B70'}
                        onChange={(e) => handleColorChange('footer_color', e.target.value)}
                        placeholder="#9D8B70"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Background Image (optional)
                    </Typography>
                    <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                      <Button
                        variant="outlined"
                        component="label"
                        size="small"
                      >
                        Upload Image
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setBackgroundImageFile(file);
                              setBackgroundImagePreview(URL.createObjectURL(file));
                            }
                          }}
                        />
                      </Button>
                      {backgroundImagePreview && (
                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                          <img
                            src={backgroundImagePreview}
                            alt="Background preview"
                            style={{
                              maxWidth: '200px',
                              maxHeight: '100px',
                              objectFit: 'cover',
                              borderRadius: '4px',
                              border: '1px solid #ddd',
                            }}
                          />
                          <Button
                            size="small"
                            color="error"
                            onClick={() => {
                              setBackgroundImageFile(null);
                              setBackgroundImagePreview(null);
                            }}
                            sx={{ position: 'absolute', top: 0, right: 0, minWidth: 'auto', p: 0.5 }}
                          >
                            ×
                          </Button>
                        </Box>
                      )}
                      {family?.background_image_url && !backgroundImagePreview && (
                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                          <img
                            src={family.background_image_url}
                            alt="Current background"
                            style={{
                              maxWidth: '200px',
                              maxHeight: '100px',
                              objectFit: 'cover',
                              borderRadius: '4px',
                              border: '1px solid #ddd',
                            }}
                          />
                          <Button
                            size="small"
                            color="error"
                            onClick={() => {
                              setBackgroundImagePreview(''); // Set to empty string to clear
                            }}
                            sx={{ position: 'absolute', top: 0, right: 0, minWidth: 'auto', p: 0.5 }}
                          >
                            ×
                          </Button>
                        </Box>
                      )}
                      {family?.background_image_url && !backgroundImagePreview && (
                        <Typography variant="caption" color="text.secondary">
                          Current background image
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Upload a background image to customize your family theme. The image will be used as the background for the app.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveColors}
                  disabled={savingColors}
                >
                  {savingColors ? 'Saving...' : 'Save Theme'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleResetColors}
                  disabled={savingColors}
                >
                  Reset to Default
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Google Calendar Integration */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Google Calendar Integration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Paste your Google Calendar embed URL below to replace the default family events calendar with your own Google Calendar. To get the URL: open Google Calendar → Settings → select a calendar → "Integrate calendar" → copy the "Embed code" src URL.
              </Typography>
              <TextField
                fullWidth
                label="Google Calendar Embed URL"
                placeholder="https://calendar.google.com/calendar/embed?src=..."
                value={googleCalendarEmbedUrl}
                onChange={(e) => setGoogleCalendarEmbedUrl(e.target.value)}
                helperText={googleCalendarEmbedUrl ? 'Both calendars will appear side by side on the Calendar page.' : 'Leave blank to show only the family events calendar.'}
              />
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveColors}
                  disabled={savingColors}
                >
                  {savingColors ? 'Saving...' : 'Save'}
                </Button>
                {googleCalendarEmbedUrl && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setGoogleCalendarEmbedUrl('')}
                    disabled={savingColors}
                  >
                    Remove Google Calendar
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Role Update Dialog */}
      <Dialog
        open={roleDialogOpen}
        onClose={() => {
          setRoleDialogOpen(false);
          setSelectedMember(null);
          setNewRole('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Member Role</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Updating role for: <strong>{selectedMember?.user_name}</strong>
          </Typography>
          <FormControl fullWidth margin="normal">
            <InputLabel>New Role</InputLabel>
            <Select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
            >
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              {selectedMember?.role === 'owner' && (
                <MenuItem value="owner">Owner</MenuItem>
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRoleDialogOpen(false);
              setSelectedMember(null);
              setNewRole('');
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleUpdateRole} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FamilyDashboard;
